import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const PRIVACY_POLICY = `
**Privacy Policy**

Last updated: ${new Date().toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})}

**1. Information We Collect**
When you purchase our digital product, we collect only the information necessary to process your transaction, including your name and email address. We do not collect sensitive personal data beyond what is required for delivery of your purchase.

**2. How We Use Your Information**
Your information is used solely to:
- Deliver your purchased digital product to your email address
- Send order confirmation and access instructions
- Respond to customer support inquiries related to your purchase

**3. Cookies**
Our website may use basic session cookies to ensure proper functionality of the checkout process. We do not use tracking cookies for advertising purposes.

**4. Third-Party Services**
We use trusted third-party payment processors to handle transactions securely. These processors are PCI-DSS compliant and we do not store your payment information on our servers.

**5. Data Sharing**
We do not sell, trade, or rent your personal information to third parties. Your data is never shared except as required to fulfill your order (e.g., payment processor).

**6. Data Security**
We implement industry-standard security measures to protect your information. All transactions are encrypted using SSL technology.

**7. Your Rights**
You have the right to request deletion of your personal data at any time. As we do not maintain marketing lists, your data is used only for order fulfillment.

**8. Changes to This Policy**
We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated revision date.
`;
const TERMS_OF_SERVICE = `
**Terms of Service**

Last updated: ${new Date().toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})}

**1. Digital Product**
"Property Tax Slayer" is a digital educational guide delivered in PDF format. Upon completing your purchase, you will receive instant access via email. No physical product will be shipped.

**2. License**
Your purchase grants you a single-user, non-transferable license to use this guide for personal, non-commercial purposes. You may not reproduce, distribute, resell, or share this material in any form.

**3. Educational Purpose**
The information provided in this guide is for educational purposes only. It does not constitute legal, financial, or tax advice. Results vary based on individual circumstances, local regulations, and the accuracy of county records. We make no guarantees of specific savings amounts.

**4. No Refund Policy**
Due to the instant digital delivery nature of this product, all sales are final. By completing your purchase, you acknowledge that you are buying a digital download and agree that no refunds will be issued once access has been delivered.

**5. Intellectual Property**
All content within this guide, including text, graphics, and methodology, is the intellectual property of DiLoré Publishing. Unauthorized reproduction or distribution is strictly prohibited and may result in legal action.

**6. Limitation of Liability**
DiLoré Publishing shall not be liable for any indirect, incidental, or consequential damages arising from the use of this guide. Our total liability shall not exceed the purchase price paid for the product.

**7. Governing Law**
These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes shall be resolved through binding arbitration.

**8. Acceptance**
By purchasing this product, you agree to be bound by these Terms of Service.
`;
function renderContent(text: string) {
  return text.trim().split('\n').map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-3" data-id="element-20" />;
    if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.split('**').length === 3) {
      const content = trimmed.slice(2, -2);
      return <h3 key={i} className="text-lg font-bold text-navy mt-6 mb-2 first:mt-0" data-id="element-21">
            {content}
          </h3>;
    }
    if (trimmed.startsWith('- ')) {
      return <li key={i} className="ml-4 text-gray-700 leading-relaxed list-disc" data-id="element-22">
            {trimmed.slice(2)}
          </li>;
    }
    return <p key={i} className="text-gray-700 leading-relaxed" data-id="element-23">
          {trimmed}
        </p>;
  });
}
interface ModalProps {
  title: string;
  content: string;
  onClose: () => void;
}
function PolicyModal({
  title,
  content,
  onClose
}: ModalProps) {
  return <AnimatePresence data-id="element-24">
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-25">
        {/* Backdrop */}
        <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-26" />

        {/* Modal */}
        <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col z-10" initial={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} data-id="element-27">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 flex-shrink-0" data-id="element-28">
            <h2 className="text-2xl font-bold text-navy" data-id="element-29">{title}</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800" aria-label="Close" data-id="element-30">
              <X className="w-5 h-5" data-id="element-31" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-6 py-6 flex-1" data-id="element-32">
            <div className="prose prose-sm max-w-none" data-id="element-33">
              {renderContent(content)}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 text-center" data-id="element-34">
            <button onClick={onClose} className="bg-navy text-white font-bold py-2 px-8 rounded-lg hover:bg-navy/90 transition-colors text-sm" data-id="element-35">
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
export function Footer() {
  const [modal, setModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="bg-[#0a1529] text-gray-500 py-8 text-center text-sm px-4" data-id="element-36">
        <div className="max-w-4xl mx-auto" data-id="element-37">
          <p className="mb-3" data-id="element-38">
            © {new Date().getFullYear()} DiLoré Publishing. All rights
            reserved.
          </p>

          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-39">
            <button onClick={() => setModal('privacy')} className="text-gray-400 hover:text-gold underline underline-offset-2 transition-colors text-xs" data-id="element-40">
              Privacy Policy
            </button>
            <span className="text-gray-700" data-id="element-41">|</span>
            <button onClick={() => setModal('terms')} className="text-gray-400 hover:text-gold underline underline-offset-2 transition-colors text-xs" data-id="element-42">
              Terms of Service
            </button>
          </div>

          <p className="text-xs max-w-2xl mx-auto opacity-70" data-id="element-43">
            Disclaimer: The information provided in this guide is for
            educational purposes only and does not constitute legal or financial
            advice. Results may vary based on individual circumstances and local
            county regulations.
          </p>
        </div>
      </footer>

      {modal === 'privacy' && <PolicyModal title="Privacy Policy" content={PRIVACY_POLICY} onClose={() => setModal(null)} data-id="element-44" />}
      {modal === 'terms' && <PolicyModal title="Terms of Service" content={TERMS_OF_SERVICE} onClose={() => setModal(null)} data-id="element-45" />}
    </>;
}